import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Grid,
  alpha,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Close as CloseIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

// Styles from App.js
const commonStyles = {
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme => alpha(theme.palette.primary.main, 0.2),
      borderRadius: '20px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      transition: 'background 0.2s ease',
      '&:hover': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
      border: 'none',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
    '&:hover': {
      '&::-webkit-scrollbar-thumb': {
        background: theme => alpha(theme.palette.primary.main, 0.3),
      }
    }
  },
};

const GROUP_COLORS = [
  '#FF4D4F',  // Red (Primary)
  '#2196F3',  // Blue
  '#4CAF50',  // Green
  '#FF9800',  // Orange
  '#9C27B0',  // Purple
  '#00BCD4',  // Cyan
  '#FF5722',  // Deep Orange
  '#795548',  // Brown
  '#607D8B',  // Blue Grey
  '#E91E63',  // Pink
];

const StatisticsDialog = ({ 
  open, 
  onClose, 
  hotels, 
  categories, 
  chainDistribution, 
  systemUsage, 
  darkMode 
}) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme => `radial-gradient(circle at top right, ${alpha(theme.palette.primary.main, 0.08)}, transparent 70%)`,
          pointerEvents: 'none',
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'primary.main',
              color: 'primary.contrastText'
            }}
          >
            <BarChartIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 500,
              color: 'text.primary',
              mb: 0.5
            }}>
              Statistics & Visualization
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze hotel data and system usage patterns
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: alpha('#FF4D4F',0.08)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ 
            px: 3,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(8px)',
            '& .MuiTab-root': {
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              fontSize: '0.875rem',
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
            }
          }}
        >
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PieChartIcon sx={{ fontSize: 20 }} />
                Chain Distribution
              </Box>
            }
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BarChartIcon sx={{ fontSize: 20 }} />
                System Usage
              </Box>
            }
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box sx={{ minHeight: 500, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                justifyContent: 'space-between',
                mb: 4
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    fontWeight: 500,
                    mb: 1
                  }}>
                    Chain Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Distribution of hotels across different chains
                  </Typography>
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    minWidth: 200
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Total Hotels
                  </Typography>
                  <Typography variant="h4" sx={{ 
                    fontFamily: 'StrawberrySansDisplay',
                    color: 'primary.main'
                  }}>
                    {hotels.length}
                  </Typography>
                </Paper>
              </Box>
              
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: 400,
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={chainDistribution}
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          innerRadius={80}
                          paddingAngle={4}
                          cornerRadius={4}
                          fill="#FF4D4F"
                          dataKey="value"
                          nameKey="name"
                          label={false}
                          stroke="none"
                        >
                          {chainDistribution.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={GROUP_COLORS[index % GROUP_COLORS.length]} 
                              stroke="none"
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <Box
                                  sx={{
                                    bgcolor: darkMode ? '#121212' : 'white',
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, darkMode ? 0.5 : 0.1)}`,
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    {payload[0].name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: payload[0].payload.fill,
                                      }}
                                    />
                                    <Typography variant="body2" color="text.secondary">
                                      {payload[0].value} hotels ({(payload[0].payload.percent * 100).toFixed(0)}%)
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: 400,
                      p: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Distribution Details
                    </Typography>
                    <Box sx={{ 
                      flex: 1, 
                      overflowY: 'auto',
                      ...commonStyles.scrollbar
                    }}>
                      {chainDistribution.map((chain, index) => (
                        <Box
                          key={chain.name}
                          sx={{
                            mb: 2,
                            '&:last-child': { mb: 0 }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: GROUP_COLORS[index % GROUP_COLORS.length],
                                mr: 1
                              }}
                            />
                            <Typography variant="body2">
                              {chain.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ ml: 'auto' }}
                            >
                              {chain.value} hotels
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              height: 4,
                              bgcolor: alpha(GROUP_COLORS[index % GROUP_COLORS.length], 0.2),
                              borderRadius: 2,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${(chain.value / hotels.length) * 100}%`,
                                bgcolor: GROUP_COLORS[index % GROUP_COLORS.length],
                                borderRadius: 2
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ minHeight: 500 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 500,
                  mb: 1
                }}>
                  System Usage Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Detailed breakdown of system usage across all hotels
                </Typography>
              </Box>

              {/* Group headers */}
              {categories
                .filter(category => systemUsage.some(s => s.group === category.title))
                .map((category, groupIndex) => {
                  const systems = systemUsage.filter(s => s.group === category.title);
                  return (
                    <Box key={category.title} sx={{ mb: 4, '&:last-child': { mb: 0 } }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          mb: 3
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 3,
                            fontFamily: 'StrawberrySansDisplay',
                            fontWeight: 500,
                            color: 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(GROUP_COLORS[groupIndex % GROUP_COLORS.length], 0.1),
                              color: GROUP_COLORS[groupIndex % GROUP_COLORS.length]
                            }}
                          >
                            <StorageIcon />
                          </Box>
                          {category.title}
                        </Typography>

                        <Grid container spacing={3}>
                          {systems.map((systemData, index) => (
                            <Grid item xs={12} md={6} key={systemData.system}>
                              <Paper
                                elevation={0}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  height: '100%'
                                }}
                              >
                                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="subtitle2">
                                    {systemData.system}
                                  </Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                  <ResponsiveContainer width="100%" height={100 + (systemData.values.length * 20)}>
                                    <BarChart
                                      data={systemData.values}
                                      layout="vertical"
                                      margin={{
                                        top: 5,
                                        right: 30,
                                        left: 100,
                                        bottom: 5,
                                      }}
                                    >
                                      <XAxis 
                                        type="number"
                                        style={{
                                          fontSize: '0.75rem',
                                          fontFamily: 'StrawberrySansText',
                                        }}
                                      />
                                      <YAxis 
                                        type="category" 
                                        dataKey="name" 
                                        width={90}
                                        style={{
                                          fontSize: '0.75rem',
                                          fontFamily: 'StrawberrySansText',
                                        }}
                                      />
                                      <RechartsTooltip 
                                        content={({ active, payload, label }) => {
                                          if (active && payload && payload.length) {
                                            return (
                                              <Box
                                                sx={{
                                                  bgcolor: darkMode ? '#121212' : 'white',
                                                  p: 2,
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 2,
                                                  boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, darkMode ? 0.5 : 0.1)}`,
                                                }}
                                              >
                                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                  {label}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <Box
                                                    sx={{
                                                      width: 8,
                                                      height: 8,
                                                      borderRadius: '50%',
                                                      bgcolor: payload[0].fill,
                                                    }}
                                                  />
                                                  <Typography variant="body2" color="text.secondary">
                                                    {payload[0].value} hotels ({((payload[0].value / hotels.length) * 100).toFixed(0)}%)
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            );
                                          }
                                          return null;
                                        }}
                                        cursor={{ 
                                          fill: alpha('#000', 0.05),
                                          radius: [4, 4, 4, 4],
                                        }}
                                      />
                                      <Bar 
                                        dataKey="count" 
                                        fill={GROUP_COLORS[(groupIndex + index) % GROUP_COLORS.length]}
                                        radius={[0, 4, 4, 0]}
                                      >
                                        {systemData.values.map((entry, i) => (
                                          <Cell 
                                            key={`cell-${i}`} 
                                            fill={alpha(GROUP_COLORS[(groupIndex + index) % GROUP_COLORS.length], Math.max(0.2, 1 - (i * 0.15)))}
                                          />
                                        ))}
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>
                    </Box>
                  );
                })}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default StatisticsDialog; 